import { useCallback, useMemo } from "react";

import { create } from "@bufbuild/protobuf";
import { styled } from "@linaria/react";

import FlexWrapper, { AlignItems, JustifyContent } from "@gx/dls/containers/FlexWrapper";
import { withTheme } from "@gx/dls/theme";
import { PropsWithTheme } from "@gx/dls/theme/types";
import { SourceStatus } from "@gx/ts-contracts/services/database/models/public_pb";
import { CreateProjectSourcesRequestSchema } from "@gx/ts-contracts/services/ontology/api/project_pb";
import { SourceResource } from "@gx/ts-contracts/services/source/resources/source_pb";

import { CreateProjectFormActionType } from "@/pages/projects/forms/create/actions";
import SelectProjectSourceCardHeader from "@/pages/projects/forms/create/components/steps/select-project-sources/components/SelectProjectSourceCardHeader";
import SelectProjectSourceCardStatus from "@/pages/projects/forms/create/components/steps/select-project-sources/components/SelectProjectSourceCardStatus";
import { useCreateProjectFormContext } from "@/pages/projects/forms/create/hooks";

interface SelectProjectSourceCardProps {
  source: SourceResource;
  isExisting?: boolean;
}

const SelectProjectSourceCardWrapper = withTheme(styled.div<
  PropsWithTheme<{ $isSelected: boolean; $isDisabled: boolean }>
>`
  display: flex;
  border-radius: 8px;
  flex-direction: column;
  width: 100%;

  background-color: ${({ theme, $isSelected }) => {
    if ($isSelected) return theme.color.background.tertiary;
    return theme.color.background.primary;
  }};

  border: 0.5px solid
    ${({ theme, $isSelected }) => {
      if ($isSelected) return theme.color.border.selected;
      return theme.color.border.primary;
    }};

  cursor: ${({ $isDisabled }) => {
    if ($isDisabled) return "default";
    return "pointer";
  }};

  opacity: ${({ $isDisabled }) => {
    if ($isDisabled) return 0.5;
    return 1;
  }};

  &:hover {
    border-color: ${({ theme, $isSelected, $isDisabled }) => {
      if ($isDisabled) {
        if ($isSelected) return theme.color.border.selected;
        return theme.color.border.primary;
      }
      if ($isSelected) return theme.color.border.selected;
      return theme.color.border.secondary;
    }};
  }
`);

const SelectProjectSourceCard = ({ source, isExisting }: SelectProjectSourceCardProps) => {
  const { state, dispatch } = useCreateProjectFormContext();

  const isSourceSelected = useMemo(() => {
    if (!state.createProjectSourcesRequest.sourceIds) {
      return false;
    }
    return state.createProjectSourcesRequest.sourceIds.includes(source.id);
  }, [source.id, state.createProjectSourcesRequest.sourceIds]);

  const isSourceSelectable = source.sourceStatus !== SourceStatus.FAILED && !isExisting;

  const handleSelectSourceChange = useCallback(() => {
    if (!isSourceSelectable) return;
    if (isSourceSelected) {
      dispatch({
        type: CreateProjectFormActionType.SET_CREATE_PROJECT_SOURCES_REQUEST,
        payload: create(CreateProjectSourcesRequestSchema, {
          ...state.createProjectSourcesRequest,
          sourceIds:
            state.createProjectSourcesRequest.sourceIds?.filter((id) => id !== source.id) ?? [],
        }),
      });
    } else {
      dispatch({
        type: CreateProjectFormActionType.SET_CREATE_PROJECT_SOURCES_REQUEST,
        payload: create(CreateProjectSourcesRequestSchema, {
          ...state.createProjectSourcesRequest,
          sourceIds: [...(state.createProjectSourcesRequest.sourceIds ?? []), source.id],
        }),
      });
    }
  }, [
    source.id,
    dispatch,
    isSourceSelected,
    state.createProjectSourcesRequest,
    isSourceSelectable,
  ]);

  return (
    <SelectProjectSourceCardWrapper
      $isSelected={isSourceSelected}
      $isDisabled={!isSourceSelectable}
      onClick={isSourceSelectable ? handleSelectSourceChange : undefined}
    >
      <FlexWrapper
        alignItems={AlignItems.CENTER}
        justifyContent={JustifyContent.SPACE_BETWEEN}
        fillWidth
        padding="12px 16px"
      >
        <SelectProjectSourceCardHeader
          source={source}
          isSelected={isSourceSelected}
          isDisabled={!isSourceSelectable}
          onSelectChange={handleSelectSourceChange}
        />
        <SelectProjectSourceCardStatus
          updatedAt={source.updatedAt}
          sourceStatus={source.sourceStatus}
          isLoading={false}
          isExisting={isExisting}
        />
      </FlexWrapper>
    </SelectProjectSourceCardWrapper>
  );
};
export default SelectProjectSourceCard;
